import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../services';

const ENDPOINTS = {
  SLACK_CONNECT: '/slack/connect',
  SLACK_CALLBACK: '/slack/admin/callback',
  SLACK_STATUS: '/slack/status',
  SLACK_DISCONNECT: '/slack/disconnect',
  SLACK_CHANNELS: '/slack/channels',
  SLACK_USERS: '/slack/users/list',
  SLACK_USERS_MAPPING: '/slack/users/mapping',
  SLACK_USERS_MAPPING_GET: '/slack/users/mapping',
  SLACK_CONFIG: '/slack/config',
  SLACK_PROJECT_MAPPING: '/slack/project-mapping',
  SLACK_PROJECT_MAPPING_BY_ID: (projectId) => `/slack/project-mapping/${projectId}`,
  SLACK_PROJECT_CONFIG_BY_ID: (projectId) => `/slack/projects/${projectId}/config`
};

const getErrorMessage = (error, fallbackMessage) => {
  const apiError =
    error?.response?.data?.error
    || error?.response?.data?.message
    || error?.response?.data?.data?.error
    || error?.response?.data?.data?.message
    || error?.response?.data?.errors?.[0]?.message
    || error?.response?.data?.errors?.[0]?.error;

  return apiError || error?.message || fallbackMessage;
};

const normalizeWorkspace = (payload) => {
  if (!payload) return null;

  const workspace = payload?.data?.workspace || payload?.workspace || payload;

  if (!workspace || typeof workspace !== 'object') {
    return null;
  }

  return {
    ...workspace,
    name: typeof workspace.name === 'string' && workspace.name
      ? workspace.name
      : (typeof workspace.team_name === 'string' ? workspace.team_name : '')
  };
};

const normalizeProjectConfig = (payload) => {
  const config = payload?.data || payload;
  return config && typeof config === 'object' ? config : null;
};

export const connectSlack = createAsyncThunk(
  'slack/connect',
  async (type = 'admin', { rejectWithValue }) => {
    try {
      const response = await api.get(`${ENDPOINTS.SLACK_CONNECT}?type=${type}`);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, 'Failed to connect to Slack'),
        code: error.response?.status
      });
    }
  }
);

export const handleSlackCallback = createAsyncThunk(
  'slack/callback',
  async ({ code, state }, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ENDPOINTS.SLACK_CALLBACK}?code=${code}&state=${state}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to complete Slack authentication'));
    }
  }
);

export const fetchSlackStatus = createAsyncThunk(
  'slack/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.SLACK_STATUS);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch Slack status'));
    }
  }
);

export const disconnectSlack = createAsyncThunk(
  'slack/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.SLACK_DISCONNECT);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to disconnect Slack'));
    }
  }
);

export const fetchSlackChannels = createAsyncThunk(
  'slack/fetchChannels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.SLACK_CHANNELS);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch Slack channels'));
    }
  }
);

export const fetchSlackUsers = createAsyncThunk(
  'slack/fetchUsers',
  async (channelId, { rejectWithValue }) => {
    try {
      const url = channelId 
        ? `${ENDPOINTS.SLACK_USERS}?channel_id=${channelId}`
        : ENDPOINTS.SLACK_USERS;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch Slack users'));
    }
  }
);

export const fetchSlackUserMappings = createAsyncThunk(
  'slack/fetchUserMappings',
  async (channelId, { rejectWithValue }) => {
    try {
      const url = channelId 
        ? `${ENDPOINTS.SLACK_USERS_MAPPING_GET}?channel_id=${channelId}`
        : ENDPOINTS.SLACK_USERS_MAPPING_GET;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch Slack user mappings'));
    }
  }
);

export const updateSlackUserMappings = createAsyncThunk(
  'slack/updateUserMappings',
  async (mappings, { rejectWithValue }) => {
    try {
      const response = await api.put(ENDPOINTS.SLACK_USERS_MAPPING, mappings);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update Slack user mappings'));
    }
  }
);

export const fetchSlackConfig = createAsyncThunk(
  'slack/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.SLACK_CONFIG);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch Slack configuration'));
    }
  }
);

export const updateSlackConfig = createAsyncThunk(
  'slack/updateConfig',
  async ({ slack_enabled }, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.SLACK_CONFIG, { slack_enabled });
      return {
        ...response.data,
        slack_enabled
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update Slack configuration'));
    }
  }
);

export const createSlackProjectMapping = createAsyncThunk(
  'slack/createProjectMapping',
  async ({ project_id, slack_channel_id, slack_channel_name }, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.SLACK_PROJECT_MAPPING, {
        project_id,
        slack_channel_id,
        slack_channel_name
      });
      return {
        ...response.data,
        data: {
          project_id,
          slack_channel_id,
          slack_channel_name: slack_channel_name || null
        }
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create Slack project mapping'));
    }
  }
);

export const updateSlackProjectMapping = createAsyncThunk(
  'slack/updateProjectMapping',
  async ({ projectId, slack_channel_id, slack_channel_name }, { rejectWithValue }) => {
    try {
      const response = await api.put(ENDPOINTS.SLACK_PROJECT_MAPPING_BY_ID(projectId), {
        slack_channel_id,
        slack_channel_name
      });
      return {
        ...response.data,
        data: {
          project_id: projectId,
          slack_channel_id,
          slack_channel_name: slack_channel_name || null
        }
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update Slack project mapping'));
    }
  }
);

export const deleteSlackProjectMapping = createAsyncThunk(
  'slack/deleteProjectMapping',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.delete(ENDPOINTS.SLACK_PROJECT_MAPPING_BY_ID(projectId));
      return {
        ...response.data,
        data: {
          project_id: projectId
        }
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete Slack project mapping'));
    }
  }
);

export const fetchSlackProjectConfig = createAsyncThunk(
  'slack/fetchProjectConfig',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.SLACK_PROJECT_CONFIG_BY_ID(projectId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch Slack project configuration'));
    }
  }
);

export const upsertSlackProjectConfig = createAsyncThunk(
  'slack/upsertProjectConfig',
  async ({ projectId, config }, { rejectWithValue }) => {
    try {
      const response = await api.put(ENDPOINTS.SLACK_PROJECT_CONFIG_BY_ID(projectId), config);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to save Slack project configuration'));
    }
  }
);

export const deleteSlackProjectConfig = createAsyncThunk(
  'slack/deleteProjectConfig',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.delete(ENDPOINTS.SLACK_PROJECT_CONFIG_BY_ID(projectId));
      return {
        ...response.data,
        data: {
          project_id: projectId,
          deleted: true
        }
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete Slack project configuration'));
    }
  }
);

const initialState = {
  isConnected: false,
  isEnabled: false,
  workspace: null,
  channels: [],
  users: [],
  userMappings: [],
  projectConfig: null,
  authUrl: null,
  loading: false,
  statusLoading: false,
  channelsLoading: false,
  usersLoading: false,
  userMappingsLoading: false,
  userMappingsSaving: false,
  globalConfigLoading: false,
  projectConfigLoading: false,
  projectConfigSaving: false,
  ignoreSlackDataResponses: false,
  error: null
};

const slackSlice = createSlice({
  name: 'slack',
  initialState,
  reducers: {
    clearSlackError: (state) => {
      state.error = null;
    },
    resetSlackState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectSlack.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(connectSlack.fulfilled, (state, action) => {
        state.loading = false;
        state.authUrl =
          action.payload?.data?.authUrl
          || action.payload?.authUrl
          || action.payload?.data?.authorization_url
          || action.payload?.authorization_url
          || null;
      })
      .addCase(connectSlack.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(handleSlackCallback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleSlackCallback.fulfilled, (state, action) => {
        state.loading = false;
        state.isConnected = true;
        state.isEnabled = true;
        state.ignoreSlackDataResponses = false;
        state.workspace = normalizeWorkspace(action.payload);
      })
      .addCase(handleSlackCallback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSlackStatus.pending, (state) => {
        state.statusLoading = true;
        state.error = null;
      })
      .addCase(fetchSlackStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.isEnabled = action.payload?.data?.enabled || action.payload?.enabled || false;
        state.isConnected = action.payload?.data?.connected || action.payload?.connected || false;

        if (state.isConnected) {
          state.ignoreSlackDataResponses = false;
          // Extract workspace data from status response
          const workspaceData = action.payload?.data?.workspace || action.payload?.workspace;
          if (workspaceData) {
            state.workspace = {
              ...workspaceData,
              name: workspaceData.team_name || workspaceData.name || ''
            };
          }
        } else {
          state.workspace = null;
          state.channels = [];
          state.users = [];
          state.userMappings = [];
          state.projectConfig = null;
          state.ignoreSlackDataResponses = true;
        }
      })
      .addCase(fetchSlackStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.error = action.payload;
      })

      .addCase(disconnectSlack.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.ignoreSlackDataResponses = true;
      })
      .addCase(disconnectSlack.fulfilled, (state) => {
        state.loading = false;
        state.isConnected = false;
        state.isEnabled = false;
        state.workspace = null;
        state.channels = [];
        state.users = [];
        state.userMappings = [];
        state.projectConfig = null;
        state.authUrl = null;
        state.channelsLoading = false;
        state.usersLoading = false;
        state.userMappingsLoading = false;
        state.ignoreSlackDataResponses = true;
      })
      .addCase(disconnectSlack.rejected, (state, action) => {
        state.loading = false;
        state.ignoreSlackDataResponses = false;
        state.error = action.payload;
      })

      .addCase(fetchSlackChannels.pending, (state) => {
        state.channelsLoading = true;
        state.error = null;
      })
      .addCase(fetchSlackChannels.fulfilled, (state, action) => {
        state.channelsLoading = false;
        if (state.ignoreSlackDataResponses) {
          return;
        }
        state.channels = action.payload?.data?.channels || action.payload?.channels || [];
      })
      .addCase(fetchSlackChannels.rejected, (state, action) => {
        state.channelsLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchSlackUsers.pending, (state) => {
        state.usersLoading = true;
        state.error = null;
      })
      .addCase(fetchSlackUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        if (state.ignoreSlackDataResponses) {
          return;
        }
        state.users = action.payload?.data?.users || action.payload?.users || [];
      })
      .addCase(fetchSlackUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchSlackUserMappings.pending, (state) => {
        state.userMappingsLoading = true;
        state.error = null;
      })
      .addCase(fetchSlackUserMappings.fulfilled, (state, action) => {
        state.userMappingsLoading = false;
        if (state.ignoreSlackDataResponses) {
          return;
        }
        state.userMappings = action.payload?.data?.mappings || action.payload?.mappings || [];
      })
      .addCase(fetchSlackUserMappings.rejected, (state, action) => {
        state.userMappingsLoading = false;
        state.error = action.payload;
      })

      .addCase(updateSlackUserMappings.pending, (state) => {
        state.userMappingsSaving = true;
        state.error = null;
      })
      .addCase(updateSlackUserMappings.fulfilled, (state) => {
        state.userMappingsSaving = false;
      })
      .addCase(updateSlackUserMappings.rejected, (state, action) => {
        state.userMappingsSaving = false;
        state.error = action.payload;
      })

      .addCase(fetchSlackConfig.pending, (state) => {
        state.globalConfigLoading = true;
        state.error = null;
      })
      .addCase(fetchSlackConfig.fulfilled, (state, action) => {
        state.globalConfigLoading = false;
        state.isEnabled = action.payload?.data?.slack_enabled ?? action.payload?.slack_enabled ?? state.isEnabled;
      })
      .addCase(fetchSlackConfig.rejected, (state, action) => {
        state.globalConfigLoading = false;
        state.error = action.payload;
      })

      .addCase(updateSlackConfig.pending, (state) => {
        state.globalConfigLoading = true;
        state.error = null;
      })
      .addCase(updateSlackConfig.fulfilled, (state, action) => {
        state.globalConfigLoading = false;
        state.isEnabled = action.payload?.data?.slack_enabled ?? action.payload?.slack_enabled ?? state.isEnabled;
      })
      .addCase(updateSlackConfig.rejected, (state, action) => {
        state.globalConfigLoading = false;
        state.error = action.payload;
      })

      .addCase(createSlackProjectMapping.pending, (state) => {
        state.projectConfigSaving = true;
        state.error = null;
      })
      .addCase(createSlackProjectMapping.fulfilled, (state) => {
        state.projectConfigSaving = false;
      })
      .addCase(createSlackProjectMapping.rejected, (state, action) => {
        state.projectConfigSaving = false;
        state.error = action.payload;
      })

      .addCase(updateSlackProjectMapping.pending, (state) => {
        state.projectConfigSaving = true;
        state.error = null;
      })
      .addCase(updateSlackProjectMapping.fulfilled, (state) => {
        state.projectConfigSaving = false;
      })
      .addCase(updateSlackProjectMapping.rejected, (state, action) => {
        state.projectConfigSaving = false;
        state.error = action.payload;
      })

      .addCase(deleteSlackProjectMapping.pending, (state) => {
        state.projectConfigSaving = true;
        state.error = null;
      })
      .addCase(deleteSlackProjectMapping.fulfilled, (state) => {
        state.projectConfigSaving = false;
        state.projectConfig = null;
      })
      .addCase(deleteSlackProjectMapping.rejected, (state, action) => {
        state.projectConfigSaving = false;
        state.error = action.payload;
      })

      .addCase(fetchSlackProjectConfig.pending, (state) => {
        state.projectConfigLoading = true;
        state.error = null;
      })
      .addCase(fetchSlackProjectConfig.fulfilled, (state, action) => {
        state.projectConfigLoading = false;
        state.projectConfig = normalizeProjectConfig(action.payload);
      })
      .addCase(fetchSlackProjectConfig.rejected, (state, action) => {
        state.projectConfigLoading = false;
        state.error = action.payload;
      })

      .addCase(upsertSlackProjectConfig.pending, (state) => {
        state.projectConfigSaving = true;
        state.error = null;
      })
      .addCase(upsertSlackProjectConfig.fulfilled, (state, action) => {
        state.projectConfigSaving = false;
        const responseConfig = normalizeProjectConfig(action.payload) || {};
        const savedConfig = action.meta?.arg?.config || {};
        const savedNotificationSetting = savedConfig.notification_setting;
        const savedProject = responseConfig.project || {};

        state.projectConfig = {
          ...state.projectConfig,
          ...responseConfig,
          ...savedConfig,
          project: {
            ...(state.projectConfig?.project || {}),
            ...savedProject,
            ...(savedNotificationSetting
              ? {
                  notification_config: {
                    ...(state.projectConfig?.project?.notification_config || {}),
                    ...savedNotificationSetting
                  }
                }
              : {})
          }
        };
      })
      .addCase(upsertSlackProjectConfig.rejected, (state, action) => {
        state.projectConfigSaving = false;
        state.error = action.payload;
      })

      .addCase(deleteSlackProjectConfig.pending, (state) => {
        state.projectConfigSaving = true;
        state.error = null;
      })
      .addCase(deleteSlackProjectConfig.fulfilled, (state) => {
        state.projectConfigSaving = false;
        state.projectConfig = null;
      })
      .addCase(deleteSlackProjectConfig.rejected, (state, action) => {
        state.projectConfigSaving = false;
        state.error = action.payload;
      });
  }
});

export const { clearSlackError, resetSlackState } = slackSlice.actions;
export default slackSlice.reducer;
